import { AddressRecord, GooglePlacesSuggestionResult } from '@navio/nsdk';
import { BookingAddress } from 'noddi-types';
import { cn, type NoddiIconName } from 'noddi-ui-common';
import { ApiErrorMessage, NoddiAsyncSelect, SelectOptionString } from 'noddi-ui-web';
import { commonTexts } from 'noddi-util';
import { useState } from 'react';
import { getLongAddressName } from '../../utils/address';
import { renderAddressSuggestionOption, useAddressSuggestions } from './addressSuggestions';
import { shouldFetchPlaceSuggestions } from './savedAddressChoices';
import { AddressSearchTranslations } from './types';

type AddressSearchProps = {
  onSelect: (address: GooglePlacesSuggestionResult) => void;
  /** Names of addresses the caller already shows. A match does not call Places. */
  savedAddressNames?: readonly string[];
  onClear?: () => void;
  translations?: AddressSearchTranslations;
  loading?: boolean;
  defaultValue?: { id: number; name: string } | AddressRecord | BookingAddress;
  fullWidthPopover?: boolean;
  countryCodes?: string[];
  startIcon?: NoddiIconName;
  /** See `keepPopoverBelowOnPhones` on `NoddiAsyncSelect`: only for a search high on its page. */
  keepPopoverBelowOnPhones?: boolean;
};

export const AddressSearch = ({
  onSelect,
  savedAddressNames = [],
  translations,
  onClear,
  loading,
  defaultValue,
  fullWidthPopover = true,
  countryCodes,
  startIcon = 'MapPin',
  keepPopoverBelowOnPhones = false
}: AddressSearchProps) => {
  const safeTrans = translations ?? commonTexts.addressSearch;
  const defaultOption = defaultValue
    ? {
        value: String(defaultValue.id),
        label: 'name' in defaultValue ? defaultValue.name : getLongAddressName(defaultValue)
      }
    : undefined;

  const [selectedOption, setSelectedOption] = useState<SelectOptionString | undefined>(defaultOption);
  const [searchTerm, setSearchTerm] = useState('');

  // Reflect the address when it is set externally (e.g. "Same as start address")
  const [prevDefaultId, setPrevDefaultId] = useState(defaultValue?.id);
  if (defaultValue?.id !== prevDefaultId) {
    setPrevDefaultId(defaultValue?.id);
    setSelectedOption(defaultOption);
  }

  const fetchPlaces = shouldFetchPlaceSuggestions(searchTerm, savedAddressNames);
  const { addressSuggestions, addressSuggestionOptions, isAddressSuggestionsLoading, addressSuggestionsError } =
    useAddressSuggestions(fetchPlaces ? searchTerm : '', countryCodes);

  if (addressSuggestionsError) {
    return <ApiErrorMessage error={addressSuggestionsError} />;
  }

  const options = fetchPlaces ? addressSuggestionOptions : [];

  const handleSelectionChange = (option: SelectOptionString | undefined) => {
    if (!option) {
      setSelectedOption(undefined);
      onClear?.();
      return;
    }

    const addressSuggestion = addressSuggestions?.find((addr) => addr.placeId === option.value);
    if (addressSuggestion) {
      setSelectedOption(option);
      onSelect(addressSuggestion);
    }
  };

  return (
    <NoddiAsyncSelect
      placeholder={safeTrans.searchForAddress}
      searchPlaceholder={safeTrans.typeToSearchAddresses}
      emptyMessage={safeTrans.noAddressesFound}
      options={options}
      selectedOption={selectedOption}
      onSelectionChange={handleSelectionChange}
      onSearch={setSearchTerm}
      isLoading={isAddressSuggestionsLoading || loading}
      renderOption={renderAddressSuggestionOption}
      startIcon={startIcon}
      popoverClassName={cn(
        fullWidthPopover && 'w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)]'
      )}
      noOptionsUnlessSearched
      keepPopoverBelowOnPhones={keepPopoverBelowOnPhones}
    />
  );
};
